import axios from 'axios';

// Punto ÚNICO de acceso a la API.
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});

// Interceptor para inyectar token Bearer en cada petición saliente
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('midas_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

/**
 * Traduce cualquier fallo a algo que la interfaz pueda mostrar.
 * No se inventan datos cuando la API falla: el error se propaga.
 */
export function describirError(error) {
  if (error.response) {
    const { status, data } = error.response;
    return {
      status,
      mensaje: data?.error || 'La operación no pudo completarse.',
      detalles: data?.detalles || null,
      possibleDuplicate: data?.possibleDuplicate === true,
      coincidencias: data?.coincidencias || null
    };
  }
  if (error.code === 'ECONNABORTED') {
    return { status: 0, mensaje: 'La conexión tardó demasiado. Revisa tu señal.', detalles: null };
  }
  return {
    status: 0,
    mensaje: 'No fue posible conectar con el servidor.',
    detalles: null
  };
}

export const healthAPI = {
  check: () => apiClient.get('/api/health')
};

// --- Autenticación y Control de Acceso (Fase 10.0) ---
const V1_AUTH = '/api/v1/auth';
export const authAPI = {
  login: (credentials) => apiClient.post(`${V1_AUTH}/login`, credentials),
  me: () => apiClient.get(`${V1_AUTH}/me`),
  changePassword: (data) => apiClient.post(`${V1_AUTH}/change-password`, data)
};

const V1_USUARIOS = '/api/v1/usuarios';
export const usuariosAPI = {
  listar: () => apiClient.get(V1_USUARIOS),
  obtener: (id) => apiClient.get(`${V1_USUARIOS}/${id}`),
  crear: (data) => apiClient.post(V1_USUARIOS, data),
  actualizar: (id, data) => apiClient.put(`${V1_USUARIOS}/${id}`, data),
  cambiarEstado: (id, activo) => apiClient.patch(`${V1_USUARIOS}/${id}/estado`, { activo }),
  resetPassword: (id, data) => apiClient.post(`${V1_USUARIOS}/${id}/reset-password`, data)
};

export const rolesAPI = {
  listar: () => apiClient.get('/api/v1/roles')
};

export const permisosAPI = {
  listar: () => apiClient.get('/api/v1/permisos')
};

const V1 = '/api/v1/clientes';
export const clientesAPI = {
  listar: (params) => apiClient.get(V1, { params }),
  obtener: (id) => apiClient.get(`${V1}/${id}`),
  creditoActual: (id) => apiClient.get(`${V1}/${id}/credito-actual`),
  expedienteIntegral: (id) => apiClient.get(`${V1}/${id}/expediente-integral`),
  crear: (data, { confirmarDuplicado = false } = {}) =>
    apiClient.post(V1, data, {
      params: confirmarDuplicado ? { confirmarDuplicado: 'true' } : undefined
    }),
  actualizar: (id, data) => apiClient.put(`${V1}/${id}`, data),
  cambiarEstado: (id, estado, motivo) =>
    apiClient.patch(`${V1}/${id}/status`, { estado, motivo: motivo || null }),
  historial: (id) => apiClient.get(`${V1}/${id}/audit`)
};

const V1_CREDITOS = '/api/v1/creditos';
export const creditosAPI = {
  listar: (params) => apiClient.get(V1_CREDITOS, { params }),
  obtener: (id) => apiClient.get(`${V1_CREDITOS}/${id}`),
  resumen: (id) => apiClient.get(`${V1_CREDITOS}/${id}/resumen`),
  calendario: (id) => apiClient.get(`${V1_CREDITOS}/${id}/calendario`),
  crear: (data) => apiClient.post(V1_CREDITOS, data),
  actualizar: (id, data) => apiClient.put(`${V1_CREDITOS}/${id}`, data),
  previsualizar: (id, overrides) => apiClient.post(`${V1_CREDITOS}/${id}/previsualizar`, overrides || {}),
  previsualizarBorrador: (datos, { signal } = {}) =>
    apiClient.post(`${V1_CREDITOS}/previsualizar`, datos, { signal }),
  autorizar: (id) => apiClient.post(`${V1_CREDITOS}/${id}/autorizar`),
  historial: (id) => apiClient.get(`${V1_CREDITOS}/${id}/audit`),
  saldo: (id) => apiClient.get(`${V1_CREDITOS}/${id}/saldo`),
  listarPagos: (id) => apiClient.get(`${V1_CREDITOS}/${id}/pagos`),
  previsualizarPago: (id, datos, { signal } = {}) =>
    apiClient.post(`${V1_CREDITOS}/${id}/pagos/previsualizar`, datos, { signal }),
  registrarPago: (id, datos) => apiClient.post(`${V1_CREDITOS}/${id}/pagos`, datos),
  listarMoratorios: (id) => apiClient.get(`${V1_CREDITOS}/${id}/moratorios`),
  situacionCobranza: (id) => apiClient.get(`${V1_CREDITOS}/${id}/cobranza`),
  listarGestiones: (id) => apiClient.get(`${V1_CREDITOS}/${id}/gestiones`),
  crearGestion: (id, datos) => apiClient.post(`${V1_CREDITOS}/${id}/gestiones`, datos),
  listarPromesas: (id) => apiClient.get(`${V1_CREDITOS}/${id}/promesas`),
  crearPromesa: (id, datos) => apiClient.post(`${V1_CREDITOS}/${id}/promesas`, datos),
  obtenerElegibilidad: (id) => apiClient.get(`${V1_CREDITOS}/${id}/elegibilidad-renovacion`),
  renovar: (id, datos) => apiClient.post(`${V1_CREDITOS}/${id}/renovar`, datos)
};

const V1_AGRUPACIONES = '/api/v1/agrupaciones';
export const agrupacionesAPI = {
  listar: (params) => apiClient.get(V1_AGRUPACIONES, { params }),
  obtener: (id) => apiClient.get(`${V1_AGRUPACIONES}/${id}`),
  pagoPeriodo: (id, params) => apiClient.get(`${V1_AGRUPACIONES}/${id}/pago-periodo`, { params }),
  creditos: (id, params) => apiClient.get(`${V1_AGRUPACIONES}/${id}/creditos`, { params }),
  integrantes: (id) => apiClient.get(`${V1_AGRUPACIONES}/${id}/integrantes`),
  cobranza: (id) => apiClient.get(`${V1_AGRUPACIONES}/${id}/cobranza`),
  pagos: (id, params) => apiClient.get(`${V1_AGRUPACIONES}/${id}/pagos`, { params }),
  sinVinculacion: (params) => apiClient.get(`${V1_AGRUPACIONES}/sin-vinculacion`, { params }),
  crear: (data) => apiClient.post(V1_AGRUPACIONES, data),
  actualizar: (id, data) => apiClient.put(`${V1_AGRUPACIONES}/${id}`, data),
  agregarCredito: (id, data) => apiClient.post(`${V1_AGRUPACIONES}/${id}/creditos`, data),
  previsualizar: (id) => apiClient.post(`${V1_AGRUPACIONES}/${id}/previsualizar`),
  autorizar: (id) => apiClient.post(`${V1_AGRUPACIONES}/${id}/autorizar`),
  historial: (id) => apiClient.get(`${V1_AGRUPACIONES}/${id}/audit`)
};

const V1_PAGOS = '/api/v1/pagos';
export const pagosAPI = {
  listarGlobal: (params) => apiClient.get(V1_PAGOS, { params }),
  obtener: (id) => apiClient.get(`${V1_PAGOS}/${id}`),
  reversar: (id, motivo, observaciones) => apiClient.post(`${V1_PAGOS}/${id}/reversar`, { motivo, ...(observaciones ? { observaciones } : {}) }),
  historial: (id) => apiClient.get(`${V1_PAGOS}/${id}/audit`)
};

const V1_MORATORIOS = '/api/v1/moratorios';
export const moratoriosAPI = {
  cancelar: (id, motivo) => apiClient.post(`${V1_MORATORIOS}/${id}/cancelar`, { motivo }),
  // P08-B0: condonación total/parcial (permiso MORATORIOS_CONDONAR).
  condonar: (creditoId, datos) => apiClient.post(`/api/v1/creditos/${creditoId}/moratorios/condonar`, datos),
  historial: (id) => apiClient.get(`${V1_MORATORIOS}/${id}/audit`)
};

const V1_COBRANZA = '/api/v1/cobranza';
export const cobranzaAPI = {
  listar: (params) => apiClient.get(V1_COBRANZA, { params }),
  resumen: () => apiClient.get(`${V1_COBRANZA}/resumen`),
  cancelarPromesa: (id, motivo) => apiClient.post(`${V1_COBRANZA}/promesas/${id}/cancelar`, { motivo })
};

const V1_PRODUCTOS = '/api/v1/productos-financieros';
export const productosFinancierosAPI = {
  listar: (params) => apiClient.get(V1_PRODUCTOS, { params }),
  obtener: (id) => apiClient.get(`${V1_PRODUCTOS}/${id}`)
};

const V1_EXPEDIENTES = '/api/v1/expedientes';
export const expedienteAPI = {
  obtenerPorCredito: (creditoId) => apiClient.get(`${V1_EXPEDIENTES}/credito/${creditoId}`),
  obtenerPorCliente: (clienteId) => apiClient.get(`${V1_EXPEDIENTES}/cliente/${clienteId}`),
  subirDocumento: (data) => apiClient.post(`${V1_EXPEDIENTES}/documentos`, data),
  reemplazar: (id, data) => apiClient.put(`${V1_EXPEDIENTES}/documentos/${id}/reemplazar`, data),
  validar: (id, data) => apiClient.put(`${V1_EXPEDIENTES}/documentos/${id}/validar`, data),
  descargarUrl: (id, descargar = false) => `${V1_EXPEDIENTES}/documentos/${id}/archivo?descargar=${descargar ? 1 : 0}`
};

// --- Prototipos / Compatibilidad ---
export const catalogosAPI = {
  getAll: () => apiClient.get('/api/catalogos'),
  getById: (id) => apiClient.get(`/api/catalogos/${id}`),
  create: (data) => apiClient.post('/api/catalogos', data),
  update: (id, data) => apiClient.put(`/api/catalogos/${id}`, data),
  delete: (id) => apiClient.delete(`/api/catalogos/${id}`)
};

export const carteraAPI = {
  listarPersonal: (params) => apiClient.get('/api/v1/cartera/personal', { params }),
  resumen: () => apiClient.get('/api/v1/cartera/resumen'),
  aging: (params) => apiClient.get('/api/v1/cartera/aging', { params }),
  agingResumen: (params) => apiClient.get('/api/v1/cartera/aging/resumen', { params }),
  ultimosMovimientos: (limit = 10) => apiClient.get('/api/v1/cartera/ultimos-movimientos', { params: { limit } })
};


export const documentosAPI = {
  listarPorCredito: (creditoId) => apiClient.get(`/api/v1/creditos/${creditoId}/documentos`),
  generarContrato: (creditoId) => apiClient.post(`/api/v1/creditos/${creditoId}/documentos/contrato`),
  generarGarantia: (creditoId) => apiClient.post(`/api/v1/creditos/${creditoId}/documentos/garantia`),
  generarPagare: (creditoId) => apiClient.post(`/api/v1/creditos/${creditoId}/documentos/pagare`),
  generarRecibo: (creditoId) => apiClient.post(`/api/v1/creditos/${creditoId}/documentos/recibo`),
  previsualizar: (creditoId, tipo) => apiClient.get(`/api/v1/creditos/${creditoId}/documentos/previsualizar/${tipo}`),
  formalizar: (documentoId) => apiClient.post(`/api/v1/documentos/${documentoId}/formalizar`),
  // Descargas AUTENTICADAS (Bearer vía apiClient; los endpoints exigen
  // DOCUMENTOS_VER). Devuelven la respuesta axios con data = Blob.
  descargarPdf: (documentoId) =>
    apiClient.get(`/api/v1/documentos/${encodeURIComponent(documentoId)}/pdf`, { responseType: 'blob', timeout: 60000 }),
  descargarPdfPorTipo: (creditoId, tipo) =>
    apiClient.get(`/api/v1/creditos/${encodeURIComponent(creditoId)}/documentos/${encodeURIComponent(tipo)}/pdf`, { responseType: 'blob', timeout: 60000 }),
  listarGarantias: (creditoId) => apiClient.get(`/api/v1/creditos/${creditoId}/garantias`),
  crearGarantia: (creditoId, data) => apiClient.post(`/api/v1/creditos/${creditoId}/garantias`, data),
  eliminarGarantia: (creditoId, garantiaId) => apiClient.delete(`/api/v1/creditos/${creditoId}/garantias/${garantiaId}`)
};

const V1_REPORTES = '/api/v1/reportes';
export const reportesAPI = {
  cobranza: (params) => apiClient.get(`${V1_REPORTES}/cobranza`, { params }),
  cobranzaDiaria: (params) => apiClient.get(`${V1_REPORTES}/cobranza`, { params }),
  // Descarga AUTENTICADA (Bearer vía apiClient) de Cobranza Diaria.
  // tipo: 'excel' | 'pdf'. Devuelve la respuesta axios con data = Blob.
  descargarCobranzaArchivo: (tipo, params = {}) =>
    apiClient.get(`${V1_REPORTES}/cobranza/${tipo === 'pdf' ? 'pdf' : 'excel'}`, {
      params,
      responseType: 'blob',
      timeout: 60000
    }),
  cierreCaja: (params) => apiClient.get(`${V1_REPORTES}/cierre-caja`, { params }),
  // Descarga AUTENTICADA (Bearer vía el interceptor de apiClient): una
  // navegación con window.open no lleva Authorization y recibe 401.
  // tipo: 'excel' | 'pdf'. Devuelve la respuesta axios con data = Blob.
  descargarCierreCajaArchivo: (tipo, params = {}) =>
    apiClient.get(`${V1_REPORTES}/cierre-caja/${tipo === 'pdf' ? 'pdf' : 'excel'}`, {
      params,
      responseType: 'blob',
      timeout: 60000
    }),
  // Phase 2C Persistent Cash Close
  obtenerMiCierre: (params) => apiClient.get(`${V1_REPORTES}/cierre-caja/mi-cierre`, { params }),
  cerrarCajaFisica: (data) => apiClient.post(`${V1_REPORTES}/cierre-caja/cerrar`, data),
  reabrirCajaFisica: (id, data) => apiClient.post(`${V1_REPORTES}/cierre-caja/${id}/reabrir`, data),
  listarCierresPersistentes: (params) => apiClient.get(`${V1_REPORTES}/cierre-caja/cierres`, { params }),
  descargarComprobanteCierrePdf: (id) =>
    apiClient.get(`${V1_REPORTES}/cierre-caja/${encodeURIComponent(id)}/comprobante-pdf`, {
      responseType: 'blob',
      timeout: 60000
    })
};

export default apiClient;

